import { signInWithGoogle } from '../firebase';
import '../components/GlobalHeader.css';
import './LoggedOut.css';
import { FaBolt, FaChartLine, FaExclamationTriangle, FaEye, FaFire, FaGoogle } from 'react-icons/fa';
import Footer from '../components/Footer';
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaPlus, FaMinus } from 'react-icons/fa';

const faqs = [
  {
    question: 'What is HypeScore?',
    answer:
      'HypeScore is a momentum-based sports signal platform. We blend stats, sentiment, and performance to detect clutch moments and uncover hype patterns before the sportsbooks catch on.',
  },
  {
    question: 'Can I use HypeScore to place bets?',
    answer:
      'HypeScore is an analytics tool, not a sportsbook. While it helps surface high-upside matchups, you’ll need to place bets on platforms like DraftKings, FanDuel, or others licensed in your state.',
  },
  {
    question: 'What sports does HypeScore cover?',
    answer: 'We currently support MLB, NBA, NFL, and NHL — with more on the way.',
  },
  {
    question: 'Is HypeScore free to use?',
    answer:
      'Access to the hype feed and game insights is free during our beta. Advanced features may require a subscription in the future.',
  },
];

function FAQItem({ index, isOpen, onToggle, question, answer }: any) {
  return (
    <div className="faq-item">
      <button className="faq-question" onClick={() => onToggle(index)}>
        <span>{question}</span>
        {isOpen ? <FaMinus /> : <FaPlus />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="faq-answer"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p>{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="faq-section">
      <h2 className="faq-header">FAQs</h2>
      {faqs.map((faq, index) => (
        <FAQItem
          key={index}
          index={index}
          isOpen={openIndex === index}
          onToggle={handleToggle}
          question={faq.question}
          answer={faq.answer}
        />
      ))}
    </section>
  );
}


export default function LoggedOut() {
  const handleLogin = async () => {
    const result = await signInWithGoogle();
    const token = await result.user.getIdToken();
    localStorage.setItem('token', token);
    window.location.reload();
  };

  return (
    <div className="landing-page">
      <section className="hero-block">
        <div className="hero-content">
        <motion.div className="app-name" initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div 
            className={`logo-img ${`logo-img--mlb`}`}
            title="HypeScore Logo">
            </div>
            <div className='title'>
              Hype<span>Score</span>
            </div>
          </motion.div>
          <motion.h1 className="hero-headline" initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            Sports data meets momentum.
          </motion.h1>
          <motion.p className="hero-subtext" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            HypeScore identifies momentum shifts, hype signals, and clutch performance using real-time sentiment and stat fusion.
          </motion.p>
          <motion.button
            onClick={handleLogin}
            className="cta-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaGoogle /> Sign in with Google
          </motion.button>
        </div>
        <div className="hero-background" />
      </section>

      <section className="metrics-banner">
        <div className="metric-tile">
          <h2>+58%</h2>
          <p>Accuracy over Vegas lines</p>
        </div>
        <div className="metric-tile">
          <h2>10,000+</h2>
          <p>Signals parsed per day</p>
        </div>
        <div className="metric-tile">
          <h2>4 Leagues</h2>
          <p>MLB, NBA, NHL, NFL coverage</p>
        </div>
      </section>

      <section className="feature-grid extended">
        <div className="feature-card">
          <FaBolt className="feature-icon" />
          <h3 className="feature-title">Clutch Score Engine</h3>
          <p className="feature-text">Live in-game stat tracking via ESPN powers a proprietary momentum score that rewards timely plays — not just box score filler.</p>
        </div>

        <div className="feature-card">
          <FaChartLine className="feature-icon" />
          <h3 className="feature-title">Headline Signal Parsing</h3>
          <p className="feature-text">We scrape and parse thousands of articles from Google News to detect team hype or negativity before Vegas reacts.</p>
        </div>

        <div className="feature-card">
          <FaEye className="feature-icon" />
          <h3 className="feature-title">Real-Time Game Feed</h3>
          <p className="feature-text">Track today’s games at a glance, complete with live hype signals, clutch trends, and performance flags.</p>
        </div>

        <div className="feature-card">
          <FaExclamationTriangle className="feature-icon" />
          <h3 className="feature-title">Underdog Watch</h3>
          <p className="feature-text">We surface teams with rising momentum and public fade narratives that often outperform their odds.</p>
        </div>

        <div className="feature-card">
          <FaGoogle className="feature-icon" />
          <h3 className="feature-title">Google News & Injury Intel</h3>
          <p className="feature-text">Our feed pulls team buzz, injuries, and breaking sentiment directly from news aggregators and beat sources.</p>
        </div>

        <div className="feature-card">
          <FaFire className="feature-icon" />
          <h3 className="feature-title">Win Probability Trends</h3>
          <p className="feature-text">See clutch probability shifts across innings, quarters, or periods — and spot the moment momentum changes sides.</p>
        </div>
      </section>


      <FAQSection />


      <Footer />
    </div>
  );
}
